<script lang="ts" setup>
import { useFirebaseStorage } from "vuefire";

import { ref as FirebaseRef, getDownloadURL } from "firebase/storage";
import { isHeicMedia } from "~/utils/is-heic-media";

const documentId = inject("documentId") as any;
const docStore = useSubmittedDocumentStore(documentId);
const { revision } = storeToRefs(docStore);

// Return either image or file as the type based on the mimetype

const isImage = computed(() => {
  return (fileObject: MediaObject) => {
    return fileObject.mimeType?.startsWith("image") || isHeicMedia(fileObject);
  };
});

const isPDF = computed(() => {
  return (fileObject: MediaObject) => {
    return fileObject.mimeType?.startsWith("application/pdf");
  };
});

const isSpreadsheet = computed(() => {
  return (fileObject: MediaObject) => {
    return (
      fileObject.mimeType?.startsWith(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) || fileObject.mimeType?.startsWith("application/vnd.ms-excel")
    );
  };
});

const fileUrl = ref<string | undefined>(undefined);

onMounted(async () => {
  const storage = useFirebaseStorage();

  if (revision.value?.fileObject == undefined) return;

  const fileRef = FirebaseRef(storage, revision.value.fileObject.fullPath);
  fileUrl.value = await getDownloadURL(fileRef);
});

const sortedFileObjects = computed(() => {
  if (revision.value?.fileObjects == undefined) return [];

  return revision.value.fileObjects;
});
</script>

<template>
  <div
    v-if="revision && (revision.fileObject || revision.fileObjects)"
    class="w-full h-full flex flex-col min-h-0 grow"
    id="file-preview"
  >
    <div class="flex flex-col grow min-h-0">
      <div
        v-if="revision.fileObject"
        class="h-full w-full flex flex-col min-h-0 grow"
      >
        <DocumentFilePreviewImage
          v-if="isImage(revision.fileObject)"
          :file-object="revision.fileObject"
          class="rounded border shadow"
        />

        <object
          v-if="fileUrl && isPDF(revision.fileObject)"
          :data="revision.fileObject.mediaHref"
          type="application/pdf"
          class="h-full w-full grow rounded-tl rounded-tr"
        />

        <DocumentFilePreviewSpreadsheet
          v-if="isSpreadsheet(revision.fileObject) && revision?.fileObject"
          :file-object="revision?.fileObject"
        />
      </div>

      <div
        v-if="revision.fileObjects"
        class="flex flex-col gap-2 h-full w-full px-1"
      >
        <span v-for="fileObject in sortedFileObjects" :key="fileObject.id">
          <DocumentFilePreviewImage
            v-if="isImage(fileObject)"
            :file-object="fileObject"
            class="rounded shadow"
          />

          <object
            v-if="fileUrl && isPDF(fileObject)"
            :data="fileObject.mediaHref"
            type="application/pdf"
            class="rounded-tl rounded-tr"
          />
          <DocumentFilePreviewSpreadsheet
            v-if="isSpreadsheet(fileObject)"
            :file-object="fileObject"
          />
        </span>
      </div>
    </div>

    <DocumentGeneralFeedback class="my-4 pb-4" />
  </div>
</template>

<style scoped></style>
